/** Canonical Query Type labels for the workspace queue table (Raise Query actions). */
export const CANONICAL_QUERY_TYPE_LABELS = [
  'Query to provider',
  'Quality check required',
  'Data import issues',
  'System / technical issues',
  'On hold',
  'Other',
] as const

export type CanonicalQueryTypeLabel = (typeof CANONICAL_QUERY_TYPE_LABELS)[number]

export type CanonicalQueryTypeKey =
  | 'query_to_provider'
  | 'quality_check'
  | 'data_import'
  | 'system_technical'
  | 'on_hold'
  | 'other'

const KEY_TO_LABEL: Record<CanonicalQueryTypeKey, CanonicalQueryTypeLabel> = {
  query_to_provider: 'Query to provider',
  quality_check: 'Quality check required',
  data_import: 'Data import issues',
  system_technical: 'System / technical issues',
  on_hold: 'On hold',
  other: 'Other',
}

/** Pill styling per canonical query type (queue table). */
export const QUERY_TYPE_PILL_STYLE: Record<CanonicalQueryTypeKey, { bg: string; color: string }> = {
  query_to_provider: { bg: '#fee2e2', color: '#991b1b' },
  quality_check: { bg: '#ede9fe', color: '#5b21b6' },
  data_import: { bg: '#e0e7ff', color: '#3730a3' },
  system_technical: { bg: '#fce7f3', color: '#9d174d' },
  on_hold: { bg: '#d1fae5', color: '#065f46' },
  other: { bg: '#e5e7eb', color: '#475569' },
}

export function resolveCanonicalQueryTypeKey(
  templateCode?: string | null,
  category?: string | null,
): CanonicalQueryTypeKey {
  switch (templateCode) {
    case 'MISSING_DOC':
    case 'DX_CLARIFY':
    case 'MOD_SUPPORT':
      return 'query_to_provider'
    case 'QUALITY_CHECK':
      return 'quality_check'
    case 'DATA_IMPORT_ISSUE':
      return 'data_import'
    case 'SYSTEM_TECHNICAL_ISSUE':
      return 'system_technical'
    case 'ON_HOLD':
      return 'on_hold'
    case 'CODER_QUERY_OTHER':
    case 'OTHER':
    case 'COMPLETE_W_QUERY':
      return 'other'
    default:
      break
  }

  switch (category) {
    case 'documentation':
    case 'clinical_clarification':
      return 'query_to_provider'
    case 'coding_clarification':
      return 'quality_check'
    case 'on_hold':
      return 'on_hold'
    case 'system':
      return 'other'
    case 'complete_with_query':
    case 'other':
      return 'other'
    default:
      return 'other'
  }
}

export function resolveCanonicalQueryTypeLabel(
  templateCode?: string | null,
  category?: string | null,
): CanonicalQueryTypeLabel {
  return KEY_TO_LABEL[resolveCanonicalQueryTypeKey(templateCode, category)]
}

export function resolveQueryTypeFromSummary(
  queries: {
    primary_template_code?: string | null
    primary_category?: string | null
    template_codes?: string[]
    categories?: string[]
  } | null | undefined,
): { key: CanonicalQueryTypeKey; label: CanonicalQueryTypeLabel } | null {
  if (!queries) return null

  const templateCode =
    queries.primary_template_code
    ?? queries.template_codes?.[0]
    ?? null
  const category =
    queries.primary_category
    ?? queries.categories?.[0]
    ?? null

  if (!templateCode && !category) return null

  const key = resolveCanonicalQueryTypeKey(templateCode, category)
  return { key, label: KEY_TO_LABEL[key] }
}
